// frontend/src/pages/Socrates/components/TemplateManagement/TemplateForm/index.js

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSave, faTimes, faCheck, 
  faExclamationTriangle, faInfoCircle, faCog,
  faFileAlt, faUsers, faGlobe, faChevronLeft, faChevronRight,
  faSliders, faFire, faUserFriends, faQuoteRight, faEye,
  faBrain, faSitemap, faPalette, faComments, faLock,
  faShield, faExclamationCircle, faFilter, faBan
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useTemplate } from '../../../contexts/TemplateContext';
import { useParameter } from '../../../contexts/ParameterContext';
import { useSocrates } from '../../../contexts/SocratesContext';
import { formatParameterName } from '../../../utils/parameterHelpers';
import LoadingSpinner from '../../common/LoadingSpinner';
import styles from './TemplateForm.module.css';

// Parameter type mapping
const PARAMETER_TYPE_MAP = {
  'token_count': 'integer',
  'creativity_level': 'number',
  'audience_options': 'object',
  'citation_styles': 'object',
  'perspective_options': 'object',
  'purpose_options': 'object',
  'reasoning_types': 'object',
  'structure_options': 'object',
  'style_options': 'object',
  'tone_options': 'object',
  'content_filtering_level': 'object',
  'content_warning_settings': 'object',
  'refusal_strategies': 'object',
  'hallucination_controls': 'object',
  'prohibited_topic_categories': 'object',
  'harm_monitoring_categories': 'object'
};

// Parameter icons
const PARAMETER_ICONS = {
  'token_count': faSliders,
  'creativity_level': faFire,
  'audience_options': faUserFriends,
  'citation_styles': faQuoteRight,
  'perspective_options': faEye,
  'purpose_options': faCog,
  'reasoning_types': faBrain,
  'structure_options': faSitemap,
  'style_options': faPalette,
  'tone_options': faComments,
  'content_filtering_level': faFilter,
  'content_warning_settings': faExclamationCircle,
  'refusal_strategies': faBan,
  'hallucination_controls': faShield,
  'prohibited_topic_categories': faExclamationTriangle,
  'harm_monitoring_categories': faShield
};

const TemplateForm = () => {
  const { t } = useLanguage();
  const { 
    selectedTemplate, 
    formMode, 
    createTemplate, 
    updateTemplate, 
    closeForm 
  } = useTemplate();
  const { fetchAvailableParameters } = useParameter();
  const { isSuperAdmin, userRole, enterpriseId } = useSocrates();
  
  const [formData, setFormData] = useState({
    template_id: '',
    template_name: '',
    template_description: '',
    parameters: {},
    token_count: 1000,
    creativity_level: 0.7,
    is_system_template: false,
    is_active: true
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableParams, setAvailableParams] = useState([]);
  const [parametersLoaded, setParametersLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('metadata');
  const [parameterTabs, setParameterTabs] = useState([]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  // Excluded categories that won't be shown as tabs
  const excludedCategories = [
    'plato_agents', 
    'plato_chat_status', 
    'plato_parameter_types',
    'plato_agent_status' // System variable - should not be part of templates
  ];

  // Refs for scrolling
  const tabsContainerRef = useRef(null);

  // Success message component
  const SuccessMessage = ({ message }) => (
    <div className={styles.successMessage}>
      <FontAwesomeIcon icon={faCheck} className={styles.successIcon} />
      <span>{message}</span>
    </div>
  );

  // Initialize form data
  useEffect(() => {
    if (formMode === 'edit' && selectedTemplate) {
      // Convert parameters array to object keyed by category
      const parametersObj = {};
      if (selectedTemplate.parameters) {
        selectedTemplate.parameters.forEach(param => {
          const categoryName = param.category_name || param.parameter_name;
          parametersObj[categoryName] = {
            ...param,
            selected_value: param.default_value || param.selected_value || 'system_default',
            parameter_type: PARAMETER_TYPE_MAP[param.parameter_name] || 'object'
          };
        });
      }

      // Extract token_count and creativity_level if they exist
      let tokenCount = 1000;
      let creativityLevel = 0.7;

      if (parametersObj['plato_token_count']) {
        tokenCount = parseInt(parametersObj['plato_token_count'].selected_value) || 1000;
        delete parametersObj['plato_token_count'];
      }

      if (parametersObj['plato_creativity_level']) {
        // Fix for creativity level - properly handle 0 value
        const creativityValue = parametersObj['plato_creativity_level'].selected_value;
        // Check if the value is a string '0' or number 0
        if (creativityValue === '0' || creativityValue === 0) {
          creativityLevel = 0;
        } else if (creativityValue) {
          creativityLevel = parseFloat(creativityValue);
        }
        delete parametersObj['plato_creativity_level'];
      }
  
      setFormData({
        template_id: selectedTemplate.template_id || '',
        template_name: selectedTemplate.template_name || '',
        template_description: selectedTemplate.template_description || '',
        parameters: parametersObj,
        token_count: tokenCount,
        creativity_level: creativityLevel,
        is_system_template: selectedTemplate.is_system_template === 'true',
        is_active: selectedTemplate.is_active !== 'false'
      });
    } else {
      // Reset form for create mode
      setFormData({
        template_id: crypto.randomUUID(),
        template_name: '',
        template_description: '',
        parameters: {},
        token_count: 1000,
        creativity_level: 0.7,
        is_system_template: false,
        is_active: true
      });
    }
    setErrors({});
    setIsSuccess(false);
  }, [formMode, selectedTemplate]);

  // Load available parameters when form opens
  useEffect(() => {
    const loadParameters = async () => {
      if (!parametersLoaded) {
        console.log('Loading available parameters for template form...');
        try {
          const params = await fetchAvailableParameters();
          setAvailableParams(params);
          setParametersLoaded(true);
          
          // Create tabs from parameters (excluding certain categories)
          // Add token_count and creativity_level as first tabs
          const customTabs = [
            {
              id: 'token_count',
              name: 'Token Count',
              category: 'plato_token_count',
              parameter: {
                parameter_id: 'plato_token_count',
                parameter_name: 'token_count',
                category_name: 'plato_token_count',
                parameter_type: 'integer',
                description: 'Maximum number of tokens in the response',
                min_value: 50,
                max_value: 150000
              }
            },
            {
              id: 'creativity_level',
              name: 'Creativity Level',
              category: 'plato_creativity_level',
              parameter: {
                parameter_id: 'plato_creativity_level',
                parameter_name: 'creativity_level',
                category_name: 'plato_creativity_level',
                parameter_type: 'number',
                description: 'Controls the randomness of the output (0 = factual, 1 = creative)',
                min_value: 0,
                max_value: 1,
                step_value: 0.1
              }
            }
          ];
          
          const standardTabs = params
            .filter(param => !excludedCategories.includes(param.category_name))
            .map(param => ({
              id: param.category_name,
              name: formatParameterName(param.parameter_name),
              category: param.category_name,
              parameter: param
            }));
          
          setParameterTabs([...customTabs, ...standardTabs]);
          console.log('Parameter tabs created:', [...customTabs, ...standardTabs]);
        } catch (error) {
          console.error('Error loading parameters:', error);
        }
      }
    };

    loadParameters();
  }, [fetchAvailableParameters, parametersLoaded]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.template_name.trim()) {
      newErrors.template_name = t('template_name_required');
    }
    
    if (formData.template_name.length > 100) {
      newErrors.template_name = t('template_name_too_long');
    }
    
    if (formData.template_description.length > 500) {
      newErrors.template_description = t('template_description_too_long');
    }
    
    if (!formData.template_id.trim()) {
      newErrors.template_id = t('template_id_required');
    }
    
    // Validate UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    if (formData.template_id && !uuidRegex.test(formData.template_id)) {
      newErrors.template_id = t('invalid_uuid_format');
    }
    
    // Validate token count
    if (formData.token_count < 50 || formData.token_count > 150000) {
      newErrors.token_count = t('token_count_range_error');
    }
    
    // Validate creativity level
    if (formData.creativity_level < 0 || formData.creativity_level > 1) {
      newErrors.creativity_level = t('creativity_level_range_error');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  // Toggle parameter inclusion
  const toggleParameter = (categoryName, parameter) => {
    setFormData(prev => {
      const newParameters = { ...prev.parameters };
      
      if (newParameters[categoryName]) {
        // Remove parameter
        delete newParameters[categoryName];
      } else {
        // Add parameter with default values
        newParameters[categoryName] = {
          parameter_id: parameter.parameter_id,
          parameter_name: parameter.parameter_name,
          category_name: parameter.category_name,
          parameter_type: PARAMETER_TYPE_MAP[parameter.parameter_name] || 'object',
          selected_value: 'system_default',
          is_required: false,
          available_options: parameter.parameter_values?.filter(val => val.status === 'active').map(val => ({
            value: val.value_key,
            label: val.description || val.value_key
          })) || []
        };
      }
      
      return {
        ...prev,
        parameters: newParameters
      };
    });
  };

  // Set parameter value
  const setParameterValue = (categoryName, value) => {
    setFormData(prev => {
      const newParameters = { ...prev.parameters };
      
      if (newParameters[categoryName]) {
        newParameters[categoryName] = {
          ...newParameters[categoryName],
          selected_value: value
        };
      }
      
      return {
        ...prev,
        parameters: newParameters
      };
    });
  };

  // Generate new UUID
  const generateNewUUID = () => {
    const newUUID = crypto.randomUUID();
    handleInputChange('template_id', newUUID);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Prevent non-super-admins from creating system templates
    if (formData.is_system_template && !isSuperAdmin) {
      setErrors({ 
        submit: t('requires_super_admin_role')
      });
      return;
    }    
    
    setIsSubmitting(true);
    setUpdateError(null);
    
    try {
      // Add token_count and creativity_level as parameters
      const tokenCountParam = {
        parameter_id: 'plato_token_count_token_count',
        parameter_name: 'token_count',
        category_name: 'plato_token_count',
        parameter_type: 'integer',
        selected_value: formData.token_count.toString(),
        default_value: formData.token_count.toString(),
        is_required: false
      };
      
      const creativityParam = {
        parameter_id: 'plato_creativity_level_creativity_level',
        parameter_name: 'creativity_level',
        category_name: 'plato_creativity_level',
        parameter_type: 'number',
        selected_value: formData.creativity_level.toString(),
        default_value: formData.creativity_level.toString(),
        is_required: false
      };
      
      // Convert parameters object back to array format for API
      const parametersArray = [
        tokenCountParam,
        creativityParam,
        ...Object.values(formData.parameters).map(param => ({
          parameter_id: param.parameter_id,
          parameter_name: param.parameter_name,
          category_name: param.category_name,
          parameter_type: param.parameter_type,
          selected_value: param.selected_value,
          default_value: param.selected_value,
          is_required: false
        }))
      ];
  
      const payload = {
        ...formData,
        parameters: parametersArray,
        enterprise_id: enterpriseId,
        user_role: userRole // Include user role
      };
      
      console.log('Submitting template:', payload);
      
      try {
        if (formMode === 'create') {
          await createTemplate(payload);
          setIsSuccess(true);
          setTimeout(() => {
            closeForm();
          }, 2000);
        } else {
          setIsUpdating(true);
          const response = await updateTemplate(formData.template_id, payload);
          
          if (response.success) {
            setUpdateSuccess(true);
            setTimeout(() => {
              closeForm();
            }, 2000);
          }
        }
      } catch (error) {
        // Check if the error message actually indicates success
        if (error.message && error.message.toLowerCase().includes('success')) {
          if (formMode === 'create') {
            setIsSuccess(true);
          } else {
            setUpdateSuccess(true);
          }
          setTimeout(() => {
            closeForm();
          }, 2000);
        } else {
          throw error; // Re-throw if it's a real error
        }
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setUpdateError(error.message || t('form_submission_error'));
      setErrors({ submit: error.message || t('form_submission_error') });
    } finally {
      setIsSubmitting(false);
      setIsUpdating(false);
    }
  };

  // Navigate tabs
  const navigateTab = (direction) => {
    const allTabs = ['metadata', ...parameterTabs.map(tab => tab.id)];
    const currentIndex = allTabs.indexOf(activeTab);
    
    if (direction === 'prev' && currentIndex > 0) {
      setActiveTab(allTabs[currentIndex - 1]);
    } else if (direction === 'next' && currentIndex < allTabs.length - 1) {
      setActiveTab(allTabs[currentIndex + 1]);
    }
  };

  // Scroll tabs
  const scrollTabs = (direction) => {
    if (tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const scrollAmount = direction === 'left' ? -300 : 300;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Render metadata tab
  const renderMetadataTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <FontAwesomeIcon icon={faFileAlt} className={styles.tabIcon} />
        <div className={styles.tabHeaderText}>
          <h3 className={styles.tabTitle}>{t('template_metadata')}</h3>
          <p className={styles.tabDescription}>{t('basic_template_information')}</p>
        </div>
      </div>
  
      <div className={styles.metadataColumns}>
        {/* Left Column - Basic Info */}
        <div className={styles.metadataColumn}>
          {/* Template ID Field */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t('template_id')}
              <span className={styles.required}>*</span>
            </label>
            <div className={styles.inputGroup}>
              <input
                type="text"
                value={formData.template_id}
                onChange={(e) => handleInputChange('template_id', e.target.value)}
                className={`${styles.input} ${errors.template_id ? styles.error : ''}`}
                placeholder={t('template_id_placeholder')}
                disabled={formMode === 'edit'}
              />
              {formMode === 'create' && (
                <button
                  type="button"
                  onClick={generateNewUUID}
                  className={styles.generateButton}
                  title={t('generate_new_uuid')}
                >
                  <FontAwesomeIcon icon={faCog} />
                </button>
              )}
            </div>
            {errors.template_id && (
              <span className={styles.errorText}>{errors.template_id}</span>
            )}
            <span className={styles.hint}>
              <FontAwesomeIcon icon={faInfoCircle} />
              {t('template_id_hint')}
            </span>
          </div>
  
          {/* Template Name Field */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t('template_name')}
              <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              value={formData.template_name}
              onChange={(e) => handleInputChange('template_name', e.target.value)}
              className={`${styles.input} ${errors.template_name ? styles.error : ''}`}
              placeholder={t('template_name_placeholder')}
              maxLength={100}
            />
            {errors.template_name && (
              <span className={styles.errorText}>{errors.template_name}</span>
            )}
            <span className={styles.charCount}>
              {formData.template_name.length}/100
            </span>
          </div>
  
          {/* Template Description Field */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              {t('template_description')}
            </label>
            <textarea
              value={formData.template_description}
              onChange={(e) => handleInputChange('template_description', e.target.value)}
              className={`${styles.textarea} ${errors.template_description ? styles.error : ''}`}
              placeholder={t('template_description_placeholder')}
              rows={4}
              maxLength={500}
            />
            {errors.template_description && (
              <span className={styles.errorText}>{errors.template_description}</span>
            )}
            <span className={styles.charCount}>
              {formData.template_description.length}/500
            </span>
          </div>
        </div>
  
        {/* Right Column - Visibility & Status */}
        <div className={styles.metadataColumn}>
          {/* System Template Section */}
          <div className={styles.metadataRightSection}>
            <div className={styles.sectionHeader}>
              <FontAwesomeIcon icon={faGlobe} className={styles.sectionIcon} />
              <div className={styles.sectionText}>
                <h4 className={styles.sectionTitle}>{t('template_visibility')}</h4>
                <p className={styles.sectionDesc}>{t('choose_template_visibility')}</p>
              </div>
            </div>
            
            <div className={styles.visibilityOptions}>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="template_visibility"
                  checked={!formData.is_system_template}
                  onChange={() => handleInputChange('is_system_template', false)}
                  className={styles.radio}
                />
                <div className={styles.optionContent}>
                  <FontAwesomeIcon icon={faUsers} className={styles.optionIcon} />
                  <div className={styles.optionText}>
                    <span className={styles.optionTitle}>{t('user_template')}</span>
                    <span className={styles.optionDesc}>{t('user_template_description')}</span>
                  </div>
                </div>
              </label>
              
              <label className={`${styles.visibilityOption} ${!isSuperAdmin ? styles.disabled : ''}`}>
                <input
                  type="radio"
                  name="template_visibility"
                  checked={formData.is_system_template}
                  onChange={() => handleInputChange('is_system_template', true)}
                  className={styles.radio}
                  disabled={!isSuperAdmin}
                />
                <div className={styles.optionContent}>
                  <FontAwesomeIcon icon={faGlobe} className={styles.optionIcon} />
                  <div className={styles.optionText}>
                    <span className={styles.optionTitle}>{t('system_template')}</span>
                    <span className={styles.optionDesc}>{t('system_template_description')}</span>
                  </div>
                </div>
                {!isSuperAdmin && (
                  <div className={styles.adminOnlyBadge} title={t('requires_super_admin_role')}>
                    <FontAwesomeIcon icon={faLock} />
                    <span>{t('admin_only')}</span>
                  </div>
                )}
              </label>
            </div>
          </div>
  
          {/* Active Status */}
          <div className={styles.metadataRightSection}>
            <div className={styles.sectionHeader}>
              <FontAwesomeIcon icon={faCog} className={styles.sectionIcon} />
              <div className={styles.sectionText}>
                <h4 className={styles.sectionTitle}>{t('template_status')}</h4>
                <p className={styles.sectionDesc}>{t('set_template_status')}</p>
              </div>
            </div>
            
            <div className={styles.statusToggleContainer}>
              <label className={styles.statusToggle}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  className={styles.toggleCheckbox}
                />
                <span className={styles.toggleSlider}></span>
                <div className={styles.toggleLabels}>
                  <span className={`${styles.toggleLabel} ${formData.is_active ? styles.active : ''}`}>
                    {t('active')}
                  </span>
                  <span className={`${styles.toggleLabel} ${!formData.is_active ? styles.active : ''}`}>
                    {t('inactive')}
                  </span>
                </div>
              </label>
              
              <span className={styles.hint}>
                <FontAwesomeIcon icon={faInfoCircle} />
                {t('active_template_hint')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render token count tab
  const renderTokenCountTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <FontAwesomeIcon icon={faSliders} className={styles.tabIcon} />
        <div className={styles.tabHeaderText}>
          <h3 className={styles.tabTitle}>
            {t('token_count')} - <span className={styles.parameterType}>{t('integer')}</span>
          </h3>
          <p className={styles.tabDescription}>
            {t('token_count_description')}
          </p>
        </div>
      </div>

      <div className={styles.sliderContainer}>
        <div className={styles.sliderHeader}>
          <span className={styles.sliderLabel}>{t('max_tokens')}</span>
          <div className={styles.sliderValueContainer}>
            <input
              type="number"
              value={formData.token_count}
              onChange={(e) => handleInputChange('token_count', parseInt(e.target.value) || 0)}
              className={styles.sliderValueInput}
              min="50"
              max="150000"
            />
            <span className={styles.sliderValueUnit}>{t('tokens')}</span>
          </div>
        </div>
        
        <div className={styles.sliderTrack}>
          <input
            type="range"
            min="50"
            max="150000"
            step="50"
            value={formData.token_count}
            onChange={(e) => handleInputChange('token_count', parseInt(e.target.value))}
            className={styles.slider}
          />
          <div className={styles.sliderMarkers}>
            <span>50</span>
            <span>10k</span>
            <span>20k</span>
            <span>30k</span>
            <span>40k</span>
            <span>50k</span>
            <span>60k</span>
            <span>70k</span>
            <span>80k</span>
            <span>90k</span>
            <span>100k</span>
            <span>110k</span>
            <span>120k</span>
            <span>130k</span>
            <span>140k</span>
            <span>150k</span>            
          </div>
        </div>
        
        {errors.token_count && (
          <span className={styles.errorText}>{errors.token_count}</span>
        )}
        
        <div className={styles.sliderHint}>
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{t('token_count_hint')}</span>
        </div>
      </div>
    </div>
  );

  // Render creativity level tab
  const renderCreativityTab = () => (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <FontAwesomeIcon icon={faFire} className={styles.tabIcon} />
        <div className={styles.tabHeaderText}>
          <h3 className={styles.tabTitle}>
            {t('creativity_level')} - <span className={styles.parameterType}>{t('number')}</span>
          </h3>
          <p className={styles.tabDescription}>
            {t('creativity_level_description')}
          </p>
        </div>
      </div>

      <div className={styles.sliderContainer}>
        <div className={styles.sliderHeader}>
          <span className={styles.sliderLabel}>{t('creativity')}</span>
          <div className={styles.sliderValueContainer}>
            <input
              type="number"
              value={formData.creativity_level}
              onChange={(e) => handleInputChange('creativity_level', parseFloat(e.target.value) || 0)}
              className={styles.sliderValueInput}
              min="0"
              max="1"
              step="0.1"
            />
          </div>
        </div>
        
        <div className={styles.sliderTrack}>
          <input
            type="range"
            min="0"
            max="1"
            step="0.1"
            value={formData.creativity_level}
            onChange={(e) => handleInputChange('creativity_level', parseFloat(e.target.value))}
            className={styles.slider}
          />
          <div className={styles.sliderMarkers}>
            <span>0</span>
            <span>0.2</span>
            <span>0.4</span>
            <span>0.6</span>
            <span>0.8</span>
            <span>1</span>
          </div>
        </div>
        
        <div className={styles.creativityLabels}>
          <span className={styles.creativityLabel}>{t('factual')}</span>
          <span className={styles.creativityLabel}>{t('creative')}</span>
        </div>
        
        {errors.creativity_level && (
          <span className={styles.errorText}>{errors.creativity_level}</span>
        )}
        
        <div className={styles.sliderHint}>
          <FontAwesomeIcon icon={faInfoCircle} />
          <span>{t('creativity_level_hint')}</span>
        </div>
      </div>
    </div>
  );

  // Render parameter tab
  const renderParameterTab = (tabData) => {
    const parameter = tabData.parameter;
    
    // Special handling for token_count and creativity_level
    if (tabData.id === 'token_count') {
      return renderTokenCountTab();
    }
    
    if (tabData.id === 'creativity_level') {
      return renderCreativityTab();
    }
    
    const isIncluded = !!formData.parameters[parameter.category_name];
    const parameterData = formData.parameters[parameter.category_name];
    const parameterType = PARAMETER_TYPE_MAP[parameter.parameter_name] || 'object';
    const parameterIcon = PARAMETER_ICONS[parameter.parameter_name] || faCog;
    
    // Get parameter label and description from language files
    const paramName = parameter.parameter_name.replace(/_options$/, '');
    const labelKey = `plato.parameters.${paramName}.label`;
    const descriptionKey = `plato.parameters.${paramName}.description`;
    const detailedDescKey = `plato.parameters.${paramName}.detailed_ui_desc`;
    
    const parameterLabel = t(labelKey, { defaultValue: formatParameterName(parameter.parameter_name) });
    const parameterDescription = t(descriptionKey, { defaultValue: parameter.description || `Configure ${tabData.name.toLowerCase()} settings` });
    const detailedDescription = t(detailedDescKey, { defaultValue: '' });
  
    return (
      <div className={styles.tabContent}>
        <div className={styles.tabHeader}>
          <FontAwesomeIcon icon={parameterIcon} className={styles.tabIcon} />
          <div className={styles.tabHeaderText}>
            <h3 className={styles.tabTitle}>
              {parameterLabel} - <span className={styles.parameterType}>{t(parameterType)}</span>
            </h3>
            <p className={styles.tabDescription}>
              {parameterDescription}
            </p>
          </div>
          <div className={styles.tabToggle}>
            <label className={styles.toggleLabel}>
              <input
                type="checkbox"
                checked={isIncluded}
                onChange={() => toggleParameter(parameter.category_name, parameter)}
                className={styles.toggleCheckbox}
              />
              <span className={styles.toggleSlider}></span>
              <span className={styles.toggleText}>
                {isIncluded ? t('included') : t('include')}
              </span>
            </label>
          </div>
        </div>
  
        {isIncluded && (
          <div className={styles.parameterConfig}>
            {/* Detailed description if available */}
            {detailedDescription && (
              <div className={styles.parameterDetailedDescription}>
                <p>{detailedDescription}</p>
              </div>
            )}
            
            {/* Value Selection */}
            {parameter.parameter_values && parameter.parameter_values.length > 0 && (
              <div className={styles.optionsGrid}>
                {parameter.parameter_values
                  .filter(val => val.status === 'active')
                  .map(val => {
                    // Try to get translated option label
                    const optionKey = `plato.parameters.${paramName}.options.${val.value_key}`;
                    const optionLabel = t(optionKey, { defaultValue: val.description || val.value_key });
                    
                    return (
                      <div 
                        key={val.value_key} 
                        className={`${styles.optionCard} ${parameterData?.selected_value === val.value_key ? styles.selected : ''}`}
                        onClick={() => setParameterValue(parameter.category_name, val.value_key)}
                      >
                        <div className={styles.optionCardContent}>
                          <div className={styles.optionCardHeader}>
                            <span className={styles.optionCardTitle}>{optionLabel}</span>
                            {parameterData?.selected_value === val.value_key && (
                              <FontAwesomeIcon icon={faCheck} className={styles.selectedIcon} />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        )}
  
        {!isIncluded && (
          <div className={styles.parameterDisabled}>
            <FontAwesomeIcon icon={faInfoCircle} className={styles.disabledIcon} />
            <p className={styles.disabledText}>
              {t('parameter_not_included_in_template')}
            </p>
            <p className={styles.disabledHint}>
              {t('toggle_above_to_include_parameter')}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Show loading spinner during initial parameter load
  if (!parametersLoaded) {
    return <LoadingSpinner size="large" message={t('loading_parameters')} />;
  }

  // Show loading spinner during submission
  if (isSubmitting || isUpdating) {
    return (
      <div className={styles.loadingOverlay}>
        <LoadingSpinner 
          size="large" 
          message={formMode === 'create' 
            ? t('creating_template') 
            : t('updating_template')} 
        />
      </div>
    );
  }

  // Show success message
  if (isSuccess || updateSuccess) {
    return (
      <div className={styles.successOverlay}>
        <div className={styles.successContent}>
          <div className={styles.successIcon}>
            <FontAwesomeIcon icon={faCheck} />
          </div>
          <h3 className={styles.successTitle}>
            {formMode === 'create' ? t('template_created') : t('template_updated')}
          </h3>
          <p className={styles.successMessage}>
            {formMode === 'create' 
              ? t('template_created_successfully') 
              : t('template_updated_successfully')}
          </p>
        </div>
      </div>
    );
  }

  const allTabs = ['metadata', ...parameterTabs.map(tab => tab.id)];
  const currentTabIndex = allTabs.indexOf(activeTab);

  return (
    <div className={styles.templateForm}>
      <form onSubmit={handleSubmit} className={styles.form}>
        {/* Tab Navigation */}
        <div className={styles.tabNavigation}>
          <button
            type="button"
            className={styles.scrollButton}
            onClick={() => scrollTabs('left')}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          
          <div className={styles.tabsList} ref={tabsContainerRef}>
            <button
              type="button"
              className={`${styles.tab} ${activeTab === 'metadata' ? styles.active : ''}`}
              onClick={() => setActiveTab('metadata')}
            >
              <FontAwesomeIcon icon={faFileAlt} />
              <span>{t('metadata')}</span>
            </button>
            
            {parameterTabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`${styles.tab} ${activeTab === tab.id ? styles.active : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <FontAwesomeIcon icon={PARAMETER_ICONS[tab.parameter.parameter_name] || faCog} />
                <span>{tab.name}</span>
                {(tab.id === 'token_count' || tab.id === 'creativity_level' || formData.parameters[tab.category]) && (
                  <span className={styles.tabIndicator}></span>
                )}
              </button>
            ))}
          </div>
          
          <button
            type="button"
            className={styles.scrollButton}
            onClick={() => scrollTabs('right')}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        {/* Tab Content */}
        <div className={styles.tabContentContainer}>
          {activeTab === 'metadata' && renderMetadataTab()}
          {parameterTabs.map(tab => 
            activeTab === tab.id && (
              <div key={tab.id}>
                {renderParameterTab(tab)}
              </div>
            )
          )}
        </div>

        {/* Form Actions */}
        <div className={styles.formActions}>
          <button
            type="button"
            onClick={closeForm}
            className={styles.cancelButton}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faTimes} />
            <span>{t('cancel')}</span>
          </button>
          
          <button
            type="submit"
            className={styles.submitButton}
            disabled={isSubmitting}
          >
            <FontAwesomeIcon icon={faSave} />
            <span>
              {formMode === 'create' 
                ? t('create_template') 
                : t('update_template')
              }
            </span>
          </button>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className={styles.submitError}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{errors.submit}</span>
          </div>
        )}

        {isSuccess && (
          <SuccessMessage message={formMode === 'create' 
            ? t('template_created_successfully') 
            : t('template_updated_successfully')} 
          />
        )}

        {updateError && (
          <div className={styles.updateError}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span>{updateError}</span>
          </div>
        )}
      </form>
    </div>
  );
};

export default TemplateForm;